import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiService } from 'services/ApiService';
import { PaymentStatus } from 'models/payments/PaymentStatus';
import { utilities } from 'utils/utilities';

const POLL_INTERVAL_MS = 1500;
const MAX_POLL_ATTEMPTS = 8;
const SUCCESS_REDIRECT_DELAY_MS = 1200;

type ReturnState = 'checking' | 'succeeded' | 'processing' | 'failed' | 'cancelled' | 'error';

/**
 * Landing page for the Stripe security deposit checkout return URL. Stripe redirects here
 * (same tab) after the customer completes or cancels hosted checkout.
 *
 * Payment completion is webhook-driven, not redirect-driven: a `status=success` redirect only
 * means Stripe itself accepted the payment, not that our local record/workflow has been
 * updated yet. This page polls the customer-safe deposit-return-context endpoint for a bounded
 * window so a webhook that is still in flight shows as "processing" rather than an error, then
 * routes the customer back into My Dashboard at the workflow instance they left off on.
 */
const DepositReturnPage: React.FC = () => {
  const { enrollmentId } = useParams<{ enrollmentId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const stripeStatus = searchParams.get('status');

  const [state, setState] = useState<ReturnState>('checking');
  const [workflowInstanceId, setWorkflowInstanceId] = useState<string | null>(null);
  const unmountedRef = useRef(false);

  const checkStatus = useCallback(async (attempt: number) => {
    if (!enrollmentId) {
      setState('error');
      return;
    }

    try {
      const payment = await ApiService.getDepositReturnContext(enrollmentId);
      if (unmountedRef.current) return;
      setWorkflowInstanceId(payment.workflowInstanceId);

      if (payment.status === PaymentStatus.Succeeded) {
        setState('succeeded');
        setTimeout(() => {
          if (!unmountedRef.current) {
            navigate(`/profile?workflowInstanceId=${payment.workflowInstanceId}`, { replace: true });
          }
        }, SUCCESS_REDIRECT_DELAY_MS);
        return;
      }

      if (payment.status === PaymentStatus.Failed) {
        setState('failed');
        return;
      }

      // Still Pending/RequiresAction — the checkout.session.completed webhook may not have
      // finished yet. Keep polling for a bounded window instead of showing an error.
      if (attempt >= MAX_POLL_ATTEMPTS) {
        setState('processing');
        return;
      }

      setState('processing');
      setTimeout(() => {
        if (!unmountedRef.current) void checkStatus(attempt + 1);
      }, POLL_INTERVAL_MS);
    } catch {
      if (!unmountedRef.current) setState('error');
    }
  }, [enrollmentId, navigate]);

  useEffect(() => {
    unmountedRef.current = false;

    if (!utilities.getUserId()) {
      navigate('/login', { replace: true });
      return;
    }

    if (stripeStatus === 'cancelled') {
      // Cancellation doesn't need to wait on a webhook — resolve the workflow instance
      // best-effort so we can still link back to the project.
      if (enrollmentId) {
        ApiService.getDepositReturnContext(enrollmentId)
          .then((payment) => { if (!unmountedRef.current) setWorkflowInstanceId(payment.workflowInstanceId); })
          .catch(() => { /* Non-fatal — the cancelled state still renders without it. */ })
          .finally(() => { if (!unmountedRef.current) setState('cancelled'); });
      } else {
        setState('cancelled');
      }
    } else {
      void checkStatus(1);
    }

    return () => { unmountedRef.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollmentId, stripeStatus]);

  const goToProject = () => {
    navigate(workflowInstanceId ? `/profile?workflowInstanceId=${workflowInstanceId}` : '/profile', { replace: true });
  };

  const checkAgain = () => {
    setState('checking');
    void checkStatus(MAX_POLL_ATTEMPTS);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center space-y-4">
        {(state === 'checking') && (
          <>
            <div className="text-3xl animate-pulse">🔄</div>
            <p className="text-base font-medium text-gray-800">Confirming your payment…</p>
            <p className="text-sm text-gray-500">Just a moment while we check with Stripe.</p>
          </>
        )}

        {state === 'succeeded' && (
          <>
            <div className="text-3xl">✅</div>
            <p className="text-base font-medium text-gray-800">Deposit received. Thank you!</p>
            <p className="text-sm text-gray-500">Taking you back to your project…</p>
          </>
        )}

        {state === 'processing' && (
          <>
            <div className="text-3xl animate-pulse">⏳</div>
            <p className="text-base font-medium text-gray-800">Your payment is still being confirmed</p>
            <p className="text-sm text-gray-500">
              This can take a few seconds. Your project will update automatically once it's done.
            </p>
            <div className="flex flex-col gap-2 pt-2">
              <button
                type="button"
                onClick={checkAgain}
                className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                Check again
              </button>
              <button
                type="button"
                onClick={goToProject}
                className="text-sm text-gray-500 hover:text-gray-700 underline focus:outline-none"
              >
                Go to my project now
              </button>
            </div>
          </>
        )}

        {state === 'failed' && (
          <>
            <div className="text-3xl">⚠️</div>
            <p className="text-base font-medium text-gray-800">Your payment didn't go through</p>
            <p className="text-sm text-gray-500">
              Stripe reported a problem processing this payment. You can try again from your project.
            </p>
            <button
              type="button"
              onClick={goToProject}
              className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              Go to my project
            </button>
          </>
        )}

        {state === 'cancelled' && (
          <>
            <div className="text-3xl">ℹ️</div>
            <p className="text-base font-medium text-gray-800">Checkout cancelled</p>
            <p className="text-sm text-gray-500">No payment was made. You can pay your deposit any time from your project.</p>
            <button
              type="button"
              onClick={goToProject}
              className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              Return to my project
            </button>
          </>
        )}

        {state === 'error' && (
          <>
            <div className="text-3xl">⚠️</div>
            <p className="text-sm text-gray-600">
              We couldn't confirm your payment status right now. Your progress has been saved — check your dashboard for the current status.
            </p>
            <button
              type="button"
              onClick={goToProject}
              className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              Go to my dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default DepositReturnPage;
